/**
 * batch-68 controle (FO-343 §4) — de pure bouwstenen van de afsprakenmotor:
 * pauze-afspraak, afronding, minimum/maximum per dag, vaste prijs en de
 * reistijdbegrenzing, plus het rekenspoor.
 *
 * Vaste volgorde (FO-343 §4.3, zo ook in het spoor):
 *   1. pauzeregel (per dagregel)  2. afronding (DAG)  3. maximum, dan minimum
 *   4. overurenstaffel  5. toeslagen.
 * Stap 2 en 3 gelden per INZETDAG: medewerker x datum x klant, over alle
 * facturabele dagregels van die dag (ook als die op verschillende werkbonnen of
 * facturen staan); het verschil komt op de laatste dagregel van de dag
 * (`pasDagMotorToe`). Stap 4 en 5 blijven in de bestaande builder
 * (`accumuleerOverurenStaffel`, de toeslag-split); deze module levert de uren die
 * daar in gaan.
 *
 * DEFAULT-UIT: zonder afspraak (`null`) geeft elke functie exact de invoer terug,
 * zodat de builder zonder motorregels byte-gelijk factureert aan voorheen.
 *
 * Pauze bouwt voort op de facturatie-as van FO-473 (`facturabeleUren`): zonder
 * PAUZE-afspraak blijft dat het urenbegrip. Met een afspraak wordt de dag gesplitst
 * in netto gewerkt (gaat de staffel in) en de factureerbare pauze (tegen het
 * 100%-tarief, telt niet mee voor de staffel: FO-343 §4.1).
 */

import { accumuleerOverurenStaffel } from './overuren-staffel'

// ─── Rekenspoor ──────────────────────────────────────────────────────────────

export const FACTURATIE_SPOOR_STAPPEN = [
  'GEREGISTREERD',
  'PAUZE',
  'AFRONDING',
  'MINIMUM',
  'MAXIMUM',
  'STAFFEL',
  'TOESLAG',
  'TARIEF',
  'REIS_RIT',
  'REIS_DAG',
  'REIS_PERIODE',
  'REIS_PERCENTAGE',
  'VASTE_PRIJS',
  'REGEL',
] as const
export type FacturatieSpoorStap = (typeof FACTURATIE_SPOOR_STAPPEN)[number]

export type SpoorWaarde = number | string | boolean | null

/** Verwijzing naar de afspraak die een stap stuurde (id + versie, FO-343 §12.1). */
export interface AfspraakRef {
  ruleId: string
  versie: number
}

export interface SpoorStap {
  /** Waar de stap over gaat: dagregel-id, `werkbonId|employeeId`, `reis:<dagregel>` ... */
  regelRef: string
  stap: FacturatieSpoorStap
  invoer: Record<string, SpoorWaarde>
  /** De afspraak die de stap stuurde; null = standaardgedrag (geen expliciete afspraak). */
  afspraakRef: AfspraakRef | null
  uitvoer: Record<string, SpoorWaarde>
  /** Een zin in gewone taal ("8,00 u aanwezig, 30 min pauze, 50% factureerbaar ..."). */
  uitleg: string
}

/**
 * Verzamelt de stappen van de motor. Productiepaden zonder spoor geven niets mee
 * en alloceren niets (de aanroepen staan achter `if (spoor)`).
 */
export interface SpoorVerzamelaar {
  readonly stappen: SpoorStap[]
  voeg(stap: SpoorStap): void
}

export function maakSpoorVerzamelaar(): SpoorVerzamelaar {
  const stappen: SpoorStap[] = []
  return {
    stappen,
    voeg(stap) {
      stappen.push(stap)
    },
  }
}

// ─── Afspraken (de motorvorm, los van Prisma) ────────────────────────────────

export const PAUZE_REGEL_SOORTEN = [
  'PAUZE_NIET_FACTUREERBAAR',
  'PAUZE_VOLLEDIG_FACTUREERBAAR',
  'PAUZE_PERCENTAGE',
  'PAUZE_MINUTEN_FACTUREERBAAR',
  'PAUZE_MINUTEN_NIET_FACTUREERBAAR',
] as const
export type PauzeRegelSoort = (typeof PAUZE_REGEL_SOORTEN)[number]

export interface PauzeFacturatieAfspraak extends AfspraakRef {
  soort: PauzeRegelSoort
  /** % (PAUZE_PERCENTAGE) of minuten (de twee MINUTEN-soorten); null bij de andere twee. */
  waarde: number | null
}

export const AFRONDING_MODI = ['OMHOOG', 'DICHTSTBIJ', 'OMLAAG'] as const
export type AfrondingModus = (typeof AFRONDING_MODI)[number]

export interface AfrondingAfspraak extends AfspraakRef {
  /** Eenheid in minuten (15, 30 of 60). */
  eenheidMinuten: number
  modus: AfrondingModus
  /** DAG = de uren van de dagregel; REGEL = de hoeveelheid van de factuurregel. */
  niveau: 'DAG' | 'REGEL'
}

export interface UrenGrensAfspraak extends AfspraakRef {
  uren: number
}

/** De afspraken die per dagregel gelden (FO-343 §4.1/§4.3). */
export interface DagAfspraken {
  pauze: PauzeFacturatieAfspraak | null
  afronding: AfrondingAfspraak | null
  minimum: UrenGrensAfspraak | null
  maximum: UrenGrensAfspraak | null
}

export function heeftDagAfspraak(a: DagAfspraken | null | undefined): a is DagAfspraken {
  return !!a && (a.pauze !== null || a.afronding !== null || a.minimum !== null || a.maximum !== null)
}

// ─── Rekenhulpen ─────────────────────────────────────────────────────────────

function r2(n: number): number {
  return Math.round(n * 100) / 100
}

const nl2 = new Intl.NumberFormat('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
function u(n: number): string {
  return `${nl2.format(n)} u`
}

/**
 * Rond uren af op een eenheid in minuten. OMHOOG en OMLAAG met een kleine
 * tolerantie (een kwartier dat door decimale ruis 0,2500001 u is, blijft een
 * kwartier); DICHTSTBIJ rondt een halve eenheid naar boven.
 */
export function rondAf(uren: number, eenheidMinuten: number, modus: AfrondingModus): number {
  if (!Number.isFinite(uren) || !(eenheidMinuten > 0)) return uren
  const eenheden = (uren * 60) / eenheidMinuten
  const eps = 1e-6
  const afgerond =
    modus === 'OMHOOG'
      ? Math.ceil(eenheden - eps)
      : modus === 'OMLAAG'
        ? Math.floor(eenheden + eps)
        : Math.floor(eenheden + 0.5 + eps)
  return r2((afgerond * eenheidMinuten) / 60)
}

/**
 * De factureerbare pauzeminuten volgens een PAUZE-afspraak (FO-343 §4.1). Ontbreekt
 * de waarde bij een soort die er een nodig heeft (de API weigert dat, maar een oude
 * of halve regel kan het hebben), dan is er niets factureerbaar: bij twijfel geen
 * pauze in rekening brengen.
 */
export function factureerbarePauzeMinuten(pauzeMinuten: number, afspraak: PauzeFacturatieAfspraak): number {
  const pauze = Math.max(0, pauzeMinuten)
  const metWaarde =
    afspraak.soort === 'PAUZE_PERCENTAGE' ||
    afspraak.soort === 'PAUZE_MINUTEN_FACTUREERBAAR' ||
    afspraak.soort === 'PAUZE_MINUTEN_NIET_FACTUREERBAAR'
  if (metWaarde && (afspraak.waarde === null || !Number.isFinite(afspraak.waarde))) return 0
  const w = afspraak.waarde ?? 0
  switch (afspraak.soort) {
    case 'PAUZE_NIET_FACTUREERBAAR':
      return 0
    case 'PAUZE_VOLLEDIG_FACTUREERBAAR':
      return pauze
    case 'PAUZE_PERCENTAGE':
      return (pauze * Math.min(100, Math.max(0, w))) / 100
    case 'PAUZE_MINUTEN_FACTUREERBAAR':
      return Math.min(pauze, Math.max(0, w))
    case 'PAUZE_MINUTEN_NIET_FACTUREERBAAR':
      return Math.max(0, pauze - Math.max(0, w))
  }
}

// ─── De dagpijplijn: per medewerker per inzetdag (FO-343 §4.1/§4.3) ─────────

/**
 * Eén dagregel als invoer van de dagmotor. De motor rekent bovenop de FO-473-
 * uren (`facturabeleUren`): de aanroeper geeft die al uitgerekend mee, zodat er
 * geen tweede pauzeberekening is (ADDENDA correctie 6).
 */
export interface DagMotorRegel {
  /** De dagregel-id; ook de `regelRef` in het spoor. */
  id: string
  /** De inzetdag: medewerker | datum | klant. Minimum, maximum en afronding gelden per dag. */
  dag: string
  /** Chronologische volgorde binnen de dag (datum, begintijd, id): de laatste krijgt het verschil. */
  volgorde: string
  /** De FO-473-uren zonder afspraak (`facturabeleUren` met de eigen factuurpauze). */
  fo473Uren: number
  /** De FO-473-uren met de HELE pauze als factuurpauze: de netto gewerkte uren. */
  nettoUren: number
  /** De feitelijke pauze van de dagregel (`pauzeMinutes`). */
  pauzeMinuten: number
  /**
   * De factuurpauze is met de hand gezet (kantoor of monteur): die wint van de
   * pauzeafspraak, anders zou de motor hem stil overschrijven.
   */
  factuurpauzeHandmatig: boolean
  /** De afspraken van deze dagregel; null = geen motorafspraak. */
  afspraken: DagAfspraken | null
}

export interface DagMotorUitkomst {
  /** Gaat de overurenstaffel en de toeslag-split in. */
  werkUren: number
  /** Factureerbare pauze (100%-tarief, buiten de staffel). */
  pauzeUren: number
  /** De uren na de pauzeregel (gewerkt + factureerbare pauze), vóór afronding, maximum en minimum. */
  naPauzeUren: number
  /**
   * De pauze die volgens de pauzeafspraak van de facturabele uren afging (minuten);
   * null = er gold geen pauzeregel (geen afspraak, of een handmatige factuurpauze).
   */
  factuurpauzeMinuten: number | null
  /** Voor de dag van deze dagregel geldt een dagminimum (ook als er niets aangevuld hoefde). */
  minimumRegel: boolean
  /** Aanvulling tot het dagminimum (factuurregel MINIMUMAFNAME); alleen op de laatste dagregel van de dag. */
  aanvulUren: number
  /** Uren boven het dagmaximum die niet gefactureerd worden (op de laatste dagregel van de dag geboekt). */
  begrensdUren: number
  /** De afronding op factuurregelniveau die voor deze dagregel geldt. */
  afrondingRegel: AfrondingAfspraak | null
}

/**
 * Gelden er afspraken over de hele dag (afronding per dag, een maximum of een minimum)?
 * Dan rekent de dag als geheel en hangt de uitkomst van elke dagregel af van de andere
 * dagregels van die dag. De compleetheidscheck van de API leest de regels zelf
 * (`isDagRegel`); een test daar bewaakt dat beide hetzelfde zeggen.
 */
export function heeftDagRegels(a: DagAfspraken | null | undefined): boolean {
  return !!a && (a.afronding?.niveau === 'DAG' || !!a.maximum || !!a.minimum)
}

/**
 * De dagmotor. Stap 1 per dagregel: de pauzeregel (bovenop FO-473; een
 * handmatige factuurpauze wint). Stap 2 per inzetdag (medewerker x datum x klant,
 * over ALLE dagregels van die dag): afronding op dagniveau, dan het maximum, dan
 * het minimum, op het dagtotaal. Het verschil gaat naar de laatste dagregel van de
 * dag (chronologisch); een begrenzing die groter is dan die dagregel, gaat door
 * naar de dagregels ervoor. De dag-afspraken (afronding, minimum, maximum) zijn
 * die van de laatste dagregel. Zonder afspraak exact de FO-473-uren.
 */
export function pasDagMotorToe(regels: readonly DagMotorRegel[], spoor?: SpoorVerzamelaar): Map<string, DagMotorUitkomst> {
  const uit = new Map<string, DagMotorUitkomst>()
  const perDag = new Map<string, DagMotorRegel[]>()
  for (const r of regels) {
    const pauzeAfspraak = r.afspraken?.pauze ?? null
    let werk = r.fo473Uren
    let pauze = 0
    let factuurpauze: number | null = null
    if (pauzeAfspraak && !r.factuurpauzeHandmatig && r.fo473Uren > 0) {
      const factMin = factureerbarePauzeMinuten(r.pauzeMinuten, pauzeAfspraak)
      werk = r2(Math.max(0, r.nettoUren))
      pauze = r2(factMin / 60)
      factuurpauze = Math.round((Math.max(0, r.pauzeMinuten) - factMin) * 100) / 100
      const aanwezig = r2(werk + Math.max(0, r.pauzeMinuten) / 60)
      spoor?.voeg({
        regelRef: r.id,
        stap: 'PAUZE',
        invoer: {
          aanwezigUren: aanwezig,
          pauzeMinuten: r.pauzeMinuten,
          zonderAfspraakUren: r.fo473Uren,
          soort: pauzeAfspraak.soort,
          waarde: pauzeAfspraak.waarde,
        },
        afspraakRef: { ruleId: pauzeAfspraak.ruleId, versie: pauzeAfspraak.versie },
        uitvoer: { nettoUren: werk, factureerbarePauzeUren: pauze, aftrekMinuten: Math.round((Math.max(0, r.pauzeMinuten) - factMin) * 100) / 100 },
        uitleg: `${u(aanwezig)} aanwezig, ${r.pauzeMinuten} min pauze: ${u(werk)} gewerkt + ${u(pauze)} factureerbare pauze (${pauzeUitleg(pauzeAfspraak)}).`,
      })
    }
    uit.set(r.id, {
      werkUren: werk,
      pauzeUren: pauze,
      naPauzeUren: r2(werk + pauze),
      factuurpauzeMinuten: factuurpauze,
      minimumRegel: false,
      aanvulUren: 0,
      begrensdUren: 0,
      afrondingRegel: r.afspraken?.afronding?.niveau === 'REGEL' ? r.afspraken.afronding : null,
    })
    const lijst = perDag.get(r.dag) ?? []
    lijst.push(r)
    perDag.set(r.dag, lijst)
  }

  for (const [dag, lijst] of perDag) {
    lijst.sort((a, b) => (a.volgorde < b.volgorde ? -1 : a.volgorde > b.volgorde ? 1 : 0))
    const laatste = lijst[lijst.length - 1]
    if (!laatste) continue
    const a = laatste.afspraken
    const afronding = a?.afronding?.niveau === 'DAG' ? a.afronding : null
    const maximum = a?.maximum ?? null
    const minimum = a?.minimum ?? null
    if (!heeftDagRegels(a)) continue
    const stand = (id: string) => uit.get(id) as DagMotorUitkomst
    if (minimum) for (const r of lijst) stand(r.id).minimumRegel = true
    const totaal = () => r2(lijst.reduce((s2, r) => s2 + stand(r.id).werkUren + stand(r.id).pauzeUren, 0))
    if (!(totaal() > 0)) continue
    /** Haal uren weg vanaf de laatste dagregel terug: eerst gewerkt, dan pauze. */
    const trekAf = (uren: number) => {
      let rest = r2(uren)
      for (let i = lijst.length - 1; i >= 0 && rest > 0; i--) {
        const st = stand((lijst[i] as DagMotorRegel).id)
        const vanWerk = Math.min(st.werkUren, rest)
        st.werkUren = r2(st.werkUren - vanWerk)
        rest = r2(rest - vanWerk)
        const vanPauze = Math.min(st.pauzeUren, rest)
        st.pauzeUren = r2(st.pauzeUren - vanPauze)
        rest = r2(rest - vanPauze)
      }
    }
    const dagTekst = dag.split('|')[1] ?? ''

    // 1. Afronding op dagniveau (het verschil gaat naar de gewerkte uren).
    if (afronding) {
      const voor = totaal()
      const afgerond = rondAf(voor, afronding.eenheidMinuten, afronding.modus)
      const verschil = r2(afgerond - voor)
      if (verschil > 0) stand(laatste.id).werkUren = r2(stand(laatste.id).werkUren + verschil)
      else if (verschil < 0) trekAf(-verschil)
      spoor?.voeg({
        regelRef: laatste.id,
        stap: 'AFRONDING',
        invoer: { uren: voor, eenheidMinuten: afronding.eenheidMinuten, modus: afronding.modus, niveau: 'DAG', dag: dagTekst, dagregels: lijst.length },
        afspraakRef: { ruleId: afronding.ruleId, versie: afronding.versie },
        uitvoer: { uren: totaal(), verschil },
        uitleg: `${u(voor)} op de dag ${modusTekst(afronding.modus)} afgerond op ${afronding.eenheidMinuten} min: ${u(totaal())}.`,
      })
    }

    // 2. Maximum per dag: het meerdere niet factureren.
    if (maximum && totaal() > maximum.uren + 1e-9) {
      const voor = totaal()
      const begrensd = r2(voor - maximum.uren)
      trekAf(begrensd)
      stand(laatste.id).begrensdUren = r2(stand(laatste.id).begrensdUren + begrensd)
      spoor?.voeg({
        regelRef: laatste.id,
        stap: 'MAXIMUM',
        invoer: { uren: voor, maximum: maximum.uren, dag: dagTekst, dagregels: lijst.length },
        afspraakRef: { ruleId: maximum.ruleId, versie: maximum.versie },
        uitvoer: { uren: totaal(), begrensdUren: begrensd },
        uitleg: `${u(voor)} op de dag, maximaal ${u(maximum.uren)}: ${u(begrensd)} niet gefactureerd.`,
      })
    }

    // 3. Minimum per dag: aanvullen tot het minimum (eigen regel).
    const naMax = totaal()
    if (minimum && naMax > 0 && naMax < minimum.uren - 1e-9) {
      const aanvul = r2(minimum.uren - naMax)
      stand(laatste.id).aanvulUren = aanvul
      spoor?.voeg({
        regelRef: laatste.id,
        stap: 'MINIMUM',
        invoer: { uren: naMax, minimum: minimum.uren, dag: dagTekst, dagregels: lijst.length },
        afspraakRef: { ruleId: minimum.ruleId, versie: minimum.versie },
        uitvoer: { aanvulUren: aanvul, uren: r2(naMax + aanvul) },
        uitleg: `${u(naMax)} op de dag, minimaal ${u(minimum.uren)}: ${u(aanvul)} minimumafname.`,
      })
    }
  }
  return uit
}

/**
 * Eén dagregel door de dagmotor (handig voor tests en losse aanroepen): de
 * FO-473-uren, de netto-uren en de pauze afgeleid uit de betaalde uren en de
 * verloningsaftrek.
 */
export interface DagFacturatieInvoer {
  regelRef: string
  /** De facturabele uren van FO-473 (`facturabeleUren`): het urenbegrip zonder afspraak. */
  facturabeleUren: number
  /** De betaalde uren van de dagregel (`hours`). */
  betaaldeUren: number
  /** De feitelijke pauze (`pauzeMinutes`). */
  pauzeMinuten: number
  /** De pauze die van de betaalde uren afging (`verloningsAftrekMinuten`). */
  verloningsAftrekMinuten: number
  factuurpauzeHandmatig?: boolean
}

export type DagFacturatieUitkomst = Pick<DagMotorUitkomst, 'werkUren' | 'pauzeUren' | 'aanvulUren' | 'begrensdUren'>

export function pasDagAfsprakenToe(
  invoer: DagFacturatieInvoer,
  afspraken: DagAfspraken | null | undefined,
  spoor?: SpoorVerzamelaar,
): DagFacturatieUitkomst {
  if (!heeftDagAfspraak(afspraken) || !(invoer.betaaldeUren > 0)) {
    return { werkUren: invoer.facturabeleUren, pauzeUren: 0, aanvulUren: 0, begrensdUren: 0 }
  }
  const netto = r2(Math.max(0, invoer.betaaldeUren + (invoer.verloningsAftrekMinuten - invoer.pauzeMinuten) / 60))
  const m = pasDagMotorToe(
    [
      {
        id: invoer.regelRef,
        dag: `-|${invoer.regelRef}|-`,
        volgorde: invoer.regelRef,
        fo473Uren: invoer.facturabeleUren,
        nettoUren: netto,
        pauzeMinuten: invoer.pauzeMinuten,
        factuurpauzeHandmatig: invoer.factuurpauzeHandmatig ?? false,
        afspraken,
      },
    ],
    spoor,
  )
  const r = m.get(invoer.regelRef) as DagMotorUitkomst
  return { werkUren: r.werkUren, pauzeUren: r.pauzeUren, aanvulUren: r.aanvulUren, begrensdUren: r.begrensdUren }
}

function pauzeUitleg(a: PauzeFacturatieAfspraak): string {
  switch (a.soort) {
    case 'PAUZE_NIET_FACTUREERBAAR':
      return 'pauze niet factureerbaar'
    case 'PAUZE_VOLLEDIG_FACTUREERBAAR':
      return 'pauze volledig factureerbaar'
    case 'PAUZE_PERCENTAGE':
      return `${a.waarde ?? 0}% van de pauze factureerbaar`
    case 'PAUZE_MINUTEN_FACTUREERBAAR':
      return `maximaal ${a.waarde ?? 0} min pauze factureerbaar`
    case 'PAUZE_MINUTEN_NIET_FACTUREERBAAR':
      return `${a.waarde ?? 0} min pauze niet factureerbaar, de rest wel`
  }
}

function modusTekst(m: AfrondingModus): string {
  return m === 'OMHOOG' ? 'naar boven' : m === 'OMLAAG' ? 'naar beneden' : 'naar de dichtstbijzijnde eenheid'
}

// ─── Vaste prijs per periode (FO-343 §4.4) ───────────────────────────────────

export interface VastePrijsAfspraak extends AfspraakRef {
  bedrag: number
  /** Inbegrepen uren; null = alle uren zitten in de vaste prijs. */
  inbegrepenUren: number | null
  meerwerkToegestaan: boolean
  projectId: string | null
}

export interface VastePrijsUitkomst {
  vastBedrag: number
  meerwerkUren: number
  /** Uren boven de inbegrepen uren zonder meerwerkvlag: commerciële reductie. */
  nietGefactureerdeUren: number
}

/** Verdeel de uren van een vaste-prijs-scope over vast bedrag, meerwerk en niet gefactureerd. */
export function verdeelVastePrijs(afspraak: VastePrijsAfspraak, uren: number): VastePrijsUitkomst {
  const totaal = r2(Math.max(0, uren))
  if (afspraak.inbegrepenUren === null) {
    return { vastBedrag: r2(afspraak.bedrag), meerwerkUren: 0, nietGefactureerdeUren: 0 }
  }
  const boven = r2(Math.max(0, totaal - afspraak.inbegrepenUren))
  return {
    vastBedrag: r2(afspraak.bedrag),
    meerwerkUren: afspraak.meerwerkToegestaan ? boven : 0,
    nietGefactureerdeUren: afspraak.meerwerkToegestaan ? 0 : boven,
  }
}

// ─── Reistijdbegrenzing (FO-343 §4.2) ────────────────────────────────────────

export const REIS_PERIODE_NIVEAUS = ['WEEK', 'MEDEWERKER_PERIODE', 'WERKBON', 'PROJECT'] as const
export type ReisPeriodeNiveau = (typeof REIS_PERIODE_NIVEAUS)[number]

export interface ReisGrens extends AfspraakRef {
  /** Maximum aantal minuten binnen de groep. */
  maxMinuten: number
}

/** Eén facturabele reiseenheid (een dagregel), in chronologische volgorde aangeleverd. */
export interface ReisPost {
  regelRef: string
  minuten: number
  /**
   * De grenzen die voor deze post gelden, per niveau met de groepssleutel (bv.
   * `2026-W15|<employee>` voor WEEK). Per post, want een afspraak kan per
   * medewerker of werkbontype verschillen; het budget is per (niveau, afspraak, groep).
   */
  grenzen: Partial<Record<ReisPeriodeNiveau, { groep: string; grens: ReisGrens }>>
}

/**
 * Chronologisch verbruikende begrenzing: per niveau, afspraak en groep een
 * budget; een post neemt wat er nog is, de rest wordt begrensd. De posten MOETEN
 * al in chronologische volgorde staan (eerdere dagen eerst).
 */
export function begrensReisPerPeriode(
  posten: readonly ReisPost[],
  spoor?: SpoorVerzamelaar,
): Map<string, { minuten: number; begrensdMinuten: number }> {
  const uit = new Map<string, { minuten: number; begrensdMinuten: number }>()
  const verbruikt = new Map<string, number>()
  for (const p of posten) {
    // Eerst wat ELK niveau nog toelaat; het kleinste restant bepaalt wat er
    // gefactureerd wordt, en precies dát gaat van elk budget af. (Een niveau mag geen
    // minuten verbruiken die een ander niveau al afkapt.)
    const niveaus = REIS_PERIODE_NIVEAUS.flatMap((niveau) => {
      const gg = p.grenzen[niveau]
      if (!gg) return []
      const sleutel = `${niveau}|${gg.grens.ruleId}|${gg.groep}`
      const al = verbruikt.get(sleutel) ?? 0
      return [{ niveau, gg, sleutel, al, rest: Math.max(0, gg.grens.maxMinuten - al) }]
    })
    const toegestaan = niveaus.reduce((m, n) => Math.min(m, n.rest), Math.max(0, p.minuten))
    if (toegestaan < p.minuten) {
      for (const n of niveaus) {
        spoor?.voeg({
          regelRef: p.regelRef,
          stap: 'REIS_PERIODE',
          invoer: { minuten: p.minuten, niveau: n.niveau, groep: n.gg.groep, maxMinuten: n.gg.grens.maxMinuten, alVerbruikt: n.al },
          afspraakRef: { ruleId: n.gg.grens.ruleId, versie: n.gg.grens.versie },
          uitvoer: { minuten: toegestaan, begrensdMinuten: p.minuten - toegestaan },
          uitleg: `${p.minuten} min reistijd, ${niveauTekst(n.niveau)} al ${n.al} van ${n.gg.grens.maxMinuten} min verbruikt: ${p.minuten - toegestaan} min begrensd.`,
        })
      }
    }
    for (const n of niveaus) verbruikt.set(n.sleutel, n.al + toegestaan)
    uit.set(p.regelRef, { minuten: toegestaan, begrensdMinuten: p.minuten - toegestaan })
  }
  return uit
}

function niveauTekst(n: ReisPeriodeNiveau): string {
  return n === 'WEEK' ? 'in deze week' : n === 'MEDEWERKER_PERIODE' ? 'in de periode' : n === 'WERKBON' ? 'op deze werkbon' : 'in dit project'
}

/** Het factureerbare deel van de reistijd volgens REISTIJD_PERCENTAGE. */
export function pasReistijdPercentageToe(minuten: number, pct: number): number {
  // Op hele minuten, net als `reisFieldsForDayEntry` de reistijd op de factuur leest:
  // zo is wat het spoor zegt ook wat er gefactureerd wordt.
  return Math.round((minuten * Math.min(100, Math.max(0, pct))) / 100)
}

// ─── Gedeelde arbeidsberekening (builder en overzicht rekenen exact gelijk) ───

/**
 * Zet de factureerbare pauze op de 100%-arbeid (FO-343 §4.1: zelfde tarief, buiten
 * de staffel) en geef de nieuwe hoeveelheid + het regelbedrag. Builder en
 * facturatie-overzicht gebruiken allebei deze functie, zodat factuur en overzicht
 * op de cent gelijk blijven.
 */
export function arbeidMetPauze(
  arbeid: { uren: number; total: number } | null,
  pauzeUren: number,
  tarief: number,
): { uren: number; total: number } | null {
  if (!(pauzeUren > 0)) return arbeid
  const uren = r2((arbeid?.uren ?? 0) + pauzeUren)
  return { uren, total: r2(uren * tarief) }
}

/** Rond een factuurregel af (AFRONDING op REGEL-niveau) en reken het bedrag opnieuw. */
export function rondRegelAf(
  regel: { uren: number; unitPrice: number },
  afronding: AfrondingAfspraak,
): { uren: number; total: number } {
  const uren = rondAf(regel.uren, afronding.eenheidMinuten, afronding.modus)
  return { uren, total: r2(uren * regel.unitPrice) }
}

/**
 * Afronding op regelniveau (FO-343 §4.3, niveau REGEL) over het TOTAAL van de regel:
 * arbeid + overuren + minimumafname. Het verschil gaat op de 100%-arbeid, zodat een
 * dag die al op het minimum staat niet nog eens naar boven gaat (review I6).
 * Geeft de nieuwe arbeid (uren + bedrag) en het verschil.
 */
export function rondRegelTotaalAf(
  invoer: { arbeidUren: number; overigeUren: number; aanvulUren: number; tarief: number },
  afronding: AfrondingAfspraak,
): { arbeidUren: number; arbeidTotal: number; totaalVoor: number; totaalNa: number; verschil: number; geklemd: boolean } {
  const totaalVoor = r2(invoer.arbeidUren + invoer.overigeUren + invoer.aanvulUren)
  const totaalNa = rondAf(totaalVoor, afronding.eenheidMinuten, afronding.modus)
  const verschil = r2(totaalNa - totaalVoor)
  // De arbeid kan niet onder nul: dan schuift de regel minder dan de afronding vraagt
  // (`geklemd`; het spoor zegt dat, en de invariant verwacht dan geen veelvoud).
  const arbeidUren = r2(Math.max(0, invoer.arbeidUren + verschil))
  const werkelijk = r2(arbeidUren - invoer.arbeidUren)
  return {
    arbeidUren,
    arbeidTotal: r2(arbeidUren * invoer.tarief),
    totaalVoor,
    totaalNa: r2(totaalVoor + werkelijk),
    verschil: werkelijk,
    geklemd: werkelijk !== verschil,
  }
}

/** De gewerkte dagen voor de staffel in het spoor: machineleesbaar ("8;9.5"), zodat de invariant ze narekent. */
export function staffelDagenTekst(dagUren: readonly number[]): string {
  return dagUren.map((u) => String(r2(u))).join(';')
}

// ─── Invarianten van het spoor (FO-343 §3.8) ─────────────────────────────────

function isPauzeSoort(v: SpoorWaarde | undefined): v is PauzeRegelSoort {
  return typeof v === 'string' && (PAUZE_REGEL_SOORTEN as readonly string[]).includes(v)
}

/**
 * Controleer of het rekenspoor met zichzelf en met zijn afspraken klopt: elke stap
 * moet precies doen wat zijn afspraak zegt (niet alleen "binnen de grens"). Lege
 * lijst = in orde. Gebruikt door de tests (fase B) en straks de facturatiecontrole
 * ("berekening wijkt af van de afspraak").
 *
 * - PAUZE: de factureerbare pauze volgt de soort en de waarde van de afspraak;
 *   netto + factureerbaar <= aanwezig; aftrek = pauze - factureerbaar.
 * - AFRONDING: veelvoud van de eenheid, in de afgesproken richting en minder dan één
 *   eenheid verschoven (DICHTSTBIJ: hooguit een halve).
 * - MAXIMUM: precies op het maximum en het begrensde deel klopt. MINIMUM: precies op
 *   het minimum en de aanvulling klopt.
 * - REIS_RIT: minuten <= max per rit x ritten (naar boven op 5). REIS_DAG: precies wat
 *   er van het dagmaximum over was. REIS_PERIODE: per niveau binnen het budget, en per
 *   regel precies het kleinste restant. REIS_PERCENTAGE: in x pct, op hele minuten.
 * - TARIEF: uurtarief + pakkettoeslag = effectief tarief. STAFFEL: de banden tellen op
 *   tot de uren en het bedrag. TOESLAG: ploeg + overig = totaal.
 * - REGEL: de onderdelen tellen op tot het regelbedrag; onder een vaste prijs is de
 *   arbeid nul.
 * - VASTE_PRIJS (per afspraak): meerwerk/niet-gefactureerd = uren boven de inbegrepen
 *   uren, naar de meerwerkvlag.
 * - Met `subtotaal`: som van de regelbedragen + vaste bedragen + meerwerk = subtotaal.
 */
export function spoorInvariantFouten(stappen: readonly SpoorStap[], opties: { subtotaal?: number } = {}): string[] {
  const fouten: string[] = []
  const n = (v: SpoorWaarde | undefined): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null)
  const EPS = 0.006
  let somRegels = 0
  let somVast = 0
  const periodePerRegel = new Map<string, SpoorStap[]>()
  for (const s of stappen) {
    const i = s.invoer
    const u = s.uitvoer
    const waar = `${s.stap} ${s.regelRef}`
    switch (s.stap) {
      case 'PAUZE': {
        const aanwezig = n(i.aanwezigUren)
        const pauze = n(i.pauzeMinuten)
        const netto = n(u.nettoUren)
        const fp = n(u.factureerbarePauzeUren)
        const aftrek = n(u.aftrekMinuten)
        if (aanwezig !== null && netto !== null && fp !== null && netto + fp > aanwezig + EPS) {
          fouten.push(`${waar}: netto + factureerbare pauze is meer dan aanwezig`)
        }
        if (pauze !== null && fp !== null && fp * 60 > Math.max(0, pauze) + 0.6) fouten.push(`${waar}: meer pauze gefactureerd dan er was`)
        if (pauze !== null && fp !== null && aftrek !== null && Math.abs(Math.max(0, pauze) - fp * 60 - aftrek) > 0.6) {
          fouten.push(`${waar}: de aftrek klopt niet met de pauze en de factureerbare pauze`)
        }
        if (isPauzeSoort(i.soort) && pauze !== null && fp !== null) {
          const verwacht = r2(factureerbarePauzeMinuten(pauze, { ruleId: '', versie: 0, soort: i.soort, waarde: n(i.waarde) }) / 60)
          if (Math.abs(verwacht - fp) > EPS) fouten.push(`${waar}: de factureerbare pauze volgt de afspraak niet`)
        }
        break
      }
      case 'AFRONDING': {
        const eenheid = n(i.eenheidMinuten)
        const voor = n(i.uren)
        const na = n(u.uren)
        if (eenheid !== null && eenheid > 0 && na !== null) {
          const eenheden = (na * 60) / eenheid
          // Geklemd (de arbeid kon niet verder omlaag): dan is de uitkomst bewust geen veelvoud.
          if (i.geklemd !== true && Math.abs(eenheden - Math.round(eenheden)) > 0.02) {
            fouten.push(`${waar}: geen veelvoud van ${eenheid} min`)
          }
          if (voor !== null) {
            const schuif = na - voor
            if (i.modus === 'OMHOOG' && schuif < -EPS) fouten.push(`${waar}: naar beneden afgerond, de afspraak zegt naar boven`)
            if (i.modus === 'OMLAAG' && schuif > EPS) fouten.push(`${waar}: naar boven afgerond, de afspraak zegt naar beneden`)
            const ruimte = i.modus === 'DICHTSTBIJ' ? eenheid / 120 : eenheid / 60
            if (Math.abs(schuif) > ruimte + EPS) fouten.push(`${waar}: verder verschoven dan de afronding toestaat`)
          }
        }
        break
      }
      case 'MAXIMUM': {
        const max = n(i.maximum)
        const voor = n(i.uren)
        const na = n(u.uren)
        const begrensd = n(u.begrensdUren)
        if (max !== null && na !== null) {
          if (na > max + EPS) fouten.push(`${waar}: boven het maximum`)
          if (voor !== null && voor > max + EPS && na < max - EPS) fouten.push(`${waar}: strenger begrensd dan het maximum`)
        }
        if (voor !== null && na !== null && begrensd !== null && Math.abs(voor - na - begrensd) > EPS) {
          fouten.push(`${waar}: het begrensde deel klopt niet`)
        }
        break
      }
      case 'MINIMUM': {
        const min = n(i.minimum)
        const voor = n(i.uren)
        const na = n(u.uren)
        const aanvul = n(u.aanvulUren)
        if (min !== null && na !== null && Math.abs(na - min) > EPS) fouten.push(`${waar}: niet aangevuld tot het minimum`)
        if (voor !== null && aanvul !== null && na !== null && Math.abs(voor + aanvul - na) > EPS) {
          fouten.push(`${waar}: de aanvulling klopt niet`)
        }
        break
      }
      case 'REIS_RIT': {
        const max = n(i.maxMinutenPerRit)
        const ritten = n(i.ritten)
        const min = n(u.minuten)
        if (max !== null && ritten !== null && min !== null && min > Math.ceil((max * ritten) / 5 - 1e-9) * 5 + 0.01) {
          fouten.push(`${waar}: meer dan het maximum per rit`)
        }
        break
      }
      case 'REIS_DAG': {
        const max = n(i.maxMinutenPerDag)
        const al = n(i.alVerbruikt)
        const voor = n(i.minuten)
        const na = n(u.minuten)
        if (max !== null && na !== null && na > max + 0.01) fouten.push(`${waar}: meer dan het maximum per dag`)
        if (max !== null && al !== null && voor !== null && na !== null && Math.abs(na - Math.min(voor, Math.max(0, max - al))) > 0.01) {
          fouten.push(`${waar}: niet wat er van het dagmaximum over was`)
        }
        break
      }
      case 'REIS_PERIODE': {
        const max = n(i.maxMinuten)
        const al = n(i.alVerbruikt)
        const min = n(u.minuten)
        if (max !== null && al !== null && min !== null && al + min > max + 0.01) fouten.push(`${waar}: over de periodegrens`)
        const lijst = periodePerRegel.get(s.regelRef) ?? []
        lijst.push(s)
        periodePerRegel.set(s.regelRef, lijst)
        break
      }
      case 'REIS_PERCENTAGE': {
        const voor = n(i.minuten)
        const pct = n(i.percentage)
        const na = n(u.minuten)
        if (voor !== null && pct !== null && na !== null && Math.abs(na - pasReistijdPercentageToe(voor, pct)) > 0.01) {
          fouten.push(`${waar}: niet het afgesproken percentage`)
        }
        break
      }
      case 'TARIEF': {
        const basis = n(i.werkbonUurtarief)
        const pakket = n(i.pakketToeslag) ?? 0
        const effectief = n(u.effectiefUurtarief)
        if (basis !== null && effectief !== null && Math.abs(basis + pakket - effectief) > EPS) {
          fouten.push(`${waar}: uurtarief + pakkettoeslag is niet het effectieve tarief`)
        }
        // Reistijd tegen de snapshot op de werkbon, anders tegen het effectieve tarief.
        const reis = n(u.reistijdTarief)
        const verwachtReis = n(i.reisUurtariefWerkbon) ?? effectief
        if (reis !== null && verwachtReis !== null && Math.abs(reis - verwachtReis) > EPS) {
          fouten.push(`${waar}: het reistijdtarief is niet de snapshot van de werkbon (of het uurtarief)`)
        }
        break
      }
      case 'STAFFEL': {
        const uren = n(i.uren)
        const bedrag = n(u.bedrag)
        const deelUren = (n(u.arbeidUren) ?? 0) + (n(u.overurenUren) ?? 0)
        const deelBedrag = (n(u.arbeidBedrag) ?? 0) + (n(u.overurenBedrag) ?? 0)
        if (uren !== null && Math.abs(uren - deelUren) > EPS) fouten.push(`${waar}: de banden tellen niet op tot de uren`)
        if (bedrag !== null && Math.abs(bedrag - deelBedrag) > EPS) fouten.push(`${waar}: de banden tellen niet op tot het bedrag`)
        // Narekenen uit de gewerkte dagen (niet bij een vast overuurtarief: dat volgt de
        // planning, niet de staffel).
        const tarief = n(i.tarief)
        const delenDagen = typeof i.dagUren === 'string' && i.dagUren !== '' ? i.dagUren.split(';').map((x) => Number(x)) : null
        // Een oud of anders geformatteerd spoor ("8,00; 9,50"): dan niet narekenen.
        const leesbaar = delenDagen !== null && delenDagen.every((x) => Number.isFinite(x))
        if (leesbaar && i.vastOveruurtarief !== true && tarief !== null) {
          const dagen = (delenDagen as number[]).filter((x) => x > 0)
          const banden = accumuleerOverurenStaffel(dagen, tarief)
          const honderd = banden.filter((b) => b.factor === 1).reduce((a, b) => a + b.uren, 0)
          const over = banden.filter((b) => b.factor !== 1).reduce((a, b) => a + b.uren, 0)
          const totaal = banden.reduce((a, b) => a + b.bedrag, 0)
          if (
            Math.abs(honderd - (n(u.arbeidUren) ?? 0)) > EPS ||
            Math.abs(over - (n(u.overurenUren) ?? 0)) > EPS ||
            Math.abs(totaal - deelBedrag) > EPS
          ) {
            fouten.push(`${waar}: de staffel volgt de gewerkte dagen niet`)
          }
        }
        break
      }
      case 'TOESLAG': {
        const totaal = n(u.totaal)
        const delen = (n(u.ploegtoeslag) ?? 0) + (n(u.overigeToeslagen) ?? 0)
        if (totaal !== null && Math.abs(delen - totaal) > EPS) fouten.push(`${waar}: ploeg + overige toeslagen is niet het totaal`)
        break
      }
      case 'REGEL': {
        const delen = ['arbeid', 'overuren', 'minimumafname', 'reiskosten', 'reistijd', 'ploegtoeslag', 'overigeToeslagen', 'materialen']
          .map((k) => n(i[k]) ?? 0)
          .reduce((a, b) => a + b, 0)
        const bedrag = n(u.bedrag)
        if (bedrag !== null) {
          somRegels += bedrag
          if (Math.abs(r2(delen) - bedrag) > EPS) fouten.push(`${waar}: de onderdelen tellen niet op tot het regelbedrag`)
        }
        if (typeof i.vastePrijs === 'string') {
          const arbeid = ['arbeid', 'overuren', 'minimumafname', 'ploegtoeslag', 'overigeToeslagen']
            .map((k) => Math.abs(n(i[k]) ?? 0))
            .reduce((a, b) => a + b, 0)
          if (arbeid > EPS) fouten.push(`${waar}: arbeid gefactureerd terwijl die onder de vaste prijs valt`)
        }
        break
      }
      case 'VASTE_PRIJS': {
        if (!s.regelRef.startsWith('vaste-prijs:')) break
        const uren = n(i.uren)
        const inbegrepen = n(i.inbegrepenUren)
        const meerwerkMag = i.meerwerkToegestaan === true
        const meer = n(u.meerwerkUren) ?? 0
        const niet = n(u.nietGefactureerdeUren) ?? 0
        somVast += (n(u.vastBedrag) ?? 0) + (n(u.meerwerk) ?? 0)
        if (uren !== null && inbegrepen !== null) {
          const boven = Math.max(0, r2(uren - inbegrepen))
          const verwachtMeer = meerwerkMag ? boven : 0
          const verwachtNiet = meerwerkMag ? 0 : boven
          if (Math.abs(meer - verwachtMeer) > EPS || Math.abs(niet - verwachtNiet) > EPS) {
            fouten.push(`${waar}: het meerwerk volgt de inbegrepen uren niet`)
          }
        }
        break
      }
      default:
        break
    }
  }
  // Periodegrenzen: een post krijgt precies het kleinste restant over zijn niveaus.
  for (const [regelRef, lijst] of periodePerRegel) {
    const eerste = lijst[0]
    if (!eerste) continue
    const voor = n(eerste.invoer.minuten)
    const na = n(eerste.uitvoer.minuten)
    if (voor === null || na === null) continue
    const rest = lijst.reduce((m, st) => {
      const max = n(st.invoer.maxMinuten)
      const al = n(st.invoer.alVerbruikt)
      return max === null || al === null ? m : Math.min(m, Math.max(0, max - al))
    }, voor)
    if (Math.abs(na - rest) > 0.01) fouten.push(`REIS_PERIODE ${regelRef}: niet precies het kleinste restant van de periodegrenzen`)
  }
  if (opties.subtotaal !== undefined && Math.abs(r2(somRegels + somVast) - opties.subtotaal) > EPS) {
    fouten.push(`totaal: regels + vaste prijzen (${r2(somRegels + somVast)}) is niet het subtotaal (${opties.subtotaal})`)
  }
  return fouten
}
